import { useState, useEffect, useRef } from 'react';
import './BookingSimulator.css';

// ── Fake data ─────────────────────────────────────────────────────────────────

const TRIPS = [
  { id: 1, transporter: 'Mehdi B.', avatar: 'M', from: 'Hamburg', to: 'Tunis', date: 'Jun 12', kg: 40, priceKg: 3.5, rating: 4.9, reviews: 34, verified: true, flag: '🇩🇪→🇹🇳' },
  { id: 2, transporter: 'Sana K.', avatar: 'S', from: 'Berlin', to: 'Sfax', date: 'Jun 15', kg: 25, priceKg: 4.0, rating: 5.0, reviews: 18, verified: true, flag: '🇩🇪→🇹🇳' },
  { id: 3, transporter: 'Yassine R.', avatar: 'Y', from: 'Munich', to: 'Sousse', date: 'Jun 18', kg: 60, priceKg: 3.2, rating: 4.8, reviews: 52, verified: true, flag: '🇩🇪→🇹🇳' },
];

const CHAT_SENDER = [
  { from: 'transporter', text: 'Hello! I received your booking request. What are you sending?', delay: 0 },
  { from: 'sender', text: 'Hi! Clothes and some medicine for my mother in Tunis 🙏', delay: 1200 },
  { from: 'transporter', text: 'Perfect, no problem. I can pick up in Hamburg on Jun 11. Does that work?', delay: 2400 },
  { from: 'sender', text: 'Yes! That works great. Thank you so much 🎉', delay: 3600 },
  { from: 'transporter', text: "Your goods are in safe hands. I'll send you a photo when collected.", delay: 4800 },
];

const CHAT_TRANSPORTER = [
  { from: 'sender', text: 'Hi! I saw your trip to Tunis. I need to send 8kg of clothes for my family.', delay: 0 },
  { from: 'transporter', text: 'Sure! I have space. Pickup in Hamburg on Jun 11, is that ok?', delay: 1200 },
  { from: 'sender', text: 'Perfect! Should I bring everything in one bag?', delay: 2400 },
  { from: 'transporter', text: "Yes, one bag is fine. Payment is secured through Transportily 👍", delay: 3600 },
  { from: 'sender', text: "Amazing. Booking confirmed — see you Jun 11! 🙌", delay: 4800 },
];

const STATUS_STEPS = [
  { icon: '📦', label: 'Goods collected', sub: 'Jun 11, 09:30' },
  { icon: '✈️', label: 'In transit', sub: 'Jun 12, 14:00' },
  { icon: '🛬', label: 'Arrived in Tunis', sub: 'Jun 12, 22:15' },
  { icon: '🎉', label: 'Delivered!', sub: 'Jun 13, 11:00' },
];

const DHL_RATE = 7.5; // €/kg approximate

// ── Main component ────────────────────────────────────────────────────────────

export default function BookingSimulator() {
  const [role, setRole]           = useState(null);         // 'sender' | 'transporter'
  const [simStep, setSimStep]     = useState(0);
  const [weight, setWeight]       = useState(10);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [statusStep, setStatusStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const chatRef = useRef(null);

  const price       = selectedTrip ? +(weight * selectedTrip.priceKg * 1.10).toFixed(2) : 0;
  const dhlPrice    = +(weight * DHL_RATE).toFixed(2);
  const savings     = +(dhlPrice - price).toFixed(2);
  const savingsPct  = dhlPrice > 0 ? Math.round((savings / dhlPrice) * 100) : 0;

  // Reset when role changes
  useEffect(() => {
    setSimStep(0);
    setSelectedTrip(null);
    setChatMessages([]);
    setStatusStep(0);
    setShowComparison(false);
  }, [role]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chatMessages]);

  function selectRole(r) {
    setRole(r);
    setSimStep(1);
  }

  function selectTrip(trip) {
    setSelectedTrip(trip);
    setShowComparison(true);
    setTimeout(() => setSimStep(2), 600);
  }

  function confirmBooking() {
    setSimStep(3);
    setIsAnimating(true);
    const msgs = role === 'sender' ? CHAT_SENDER : CHAT_TRANSPORTER;
    msgs.forEach((msg) => {
      setTimeout(() => {
        setChatMessages(prev => [...prev, msg]);
      }, msg.delay + 400);
    });
    // After chat, show tracker
    setTimeout(() => {
      setSimStep(4);
      animateTracker();
    }, 6000);
  }

  function animateTracker() {
    STATUS_STEPS.forEach((_, i) => {
      setTimeout(() => setStatusStep(i + 1), i * 900);
    });
    setTimeout(() => setSimStep(5), STATUS_STEPS.length * 900 + 400);
  }

  function restart() {
    setRole(null);
    setSimStep(0);
    setSelectedTrip(null);
    setChatMessages([]);
    setStatusStep(0);
    setShowComparison(false);
    setIsAnimating(false);
    setWeight(10);
  }

  return (
    <div className="sim-wrap">
      <div className="sim-header">
        <span className="sim-tag">🎮 Live Demo</span>
        <h2 className="sim-title">Try Transportily <em>right now</em></h2>
        <p className="sim-sub">Experience the full booking flow — no signup required.</p>
      </div>

      <div className="sim-shell">
        {/* Phone frame */}
        <div className="sim-phone">
          <div className="sim-notch" />
          <div className="sim-screen">

            {/* ── STEP 0: Role picker ── */}
            {simStep === 0 && (
              <div className="sim-pane sim-pane-enter">
                <div className="sim-screen-header">
                  <div className="sim-logo">Transport<span>ily</span></div>
                </div>
                <div className="sim-role-hero">
                  <div className="sim-role-emoji">🇩🇪 ⟷ 🇹🇳</div>
                  <p className="sim-role-prompt">How would you use Transportily?</p>
                </div>
                <div className="sim-role-btns">
                  <button className="sim-role-btn sim-role-sender" onClick={() => selectRole('sender')}>
                    <span className="sim-role-icon">📦</span>
                    <strong>I want to send</strong>
                    <span>Send goods to Tunisia</span>
                  </button>
                  <button className="sim-role-btn sim-role-transporter" onClick={() => selectRole('transporter')}>
                    <span className="sim-role-icon">✈️</span>
                    <strong>I want to carry</strong>
                    <span>Earn on your next trip</span>
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 1 (SENDER): Browse trips ── */}
            {simStep === 1 && role === 'sender' && (
              <div className="sim-pane sim-pane-enter">
                <div className="sim-screen-header">
                  <button className="sim-back" onClick={restart}>←</button>
                  <span>Find a trip</span>
                </div>
                <div className="sim-weight-row">
                  <div className="sim-weight-label">
                    <span>I want to send</span>
                    <strong>{weight} kg</strong>
                  </div>
                  <input
                    type="range" min={1} max={30} value={weight}
                    onChange={e => setWeight(+e.target.value)}
                    className="sim-slider"
                  />
                  {showComparison && selectedTrip && (
                    <div className="sim-price-compare sim-price-enter">
                      <div className="sim-price-dhl">
                        <span>DHL</span>
                        <strong>€{(weight * DHL_RATE).toFixed(0)}</strong>
                      </div>
                      <div className="sim-price-arrow">→</div>
                      <div className="sim-price-us">
                        <span>Transportily</span>
                        <strong>€{(weight * selectedTrip.priceKg * 1.10).toFixed(0)}</strong>
                      </div>
                      <div className="sim-savings">Save {savingsPct}%</div>
                    </div>
                  )}
                </div>
                <div className="sim-trips">
                  {TRIPS.map((trip, i) => (
                    <button
                      key={trip.id}
                      className={`sim-trip-card${selectedTrip?.id === trip.id ? ' selected' : ''}`}
                      style={{ animationDelay: `${i * 0.08}s` }}
                      onClick={() => selectTrip(trip)}
                    >
                      <div className="sim-trip-top">
                        <div className="sim-trip-avatar">{trip.avatar}</div>
                        <div className="sim-trip-info">
                          <div className="sim-trip-name">{trip.transporter} {trip.verified && <span className="sim-verified">✓</span>}</div>
                          <div className="sim-trip-route">{trip.from} → {trip.to} · {trip.date}</div>
                        </div>
                        <div className="sim-trip-price">
                          <strong>€{trip.priceKg}</strong>
                          <span>/kg</span>
                        </div>
                      </div>
                      <div className="sim-trip-bot">
                        <span className="sim-trip-rating">⭐ {trip.rating} ({trip.reviews})</span>
                        <span className="sim-trip-cap">{trip.kg}kg available</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── STEP 1 (TRANSPORTER): Trip posted ── */}
            {simStep === 1 && role === 'transporter' && (
              <div className="sim-pane sim-pane-enter">
                <div className="sim-screen-header">
                  <button className="sim-back" onClick={restart}>←</button>
                  <span>Your trips</span>
                </div>
                <div className="sim-posted-trip">
                  <div className="sim-posted-badge">✈️ Active Trip</div>
                  <div className="sim-posted-route">Hamburg → Tunis</div>
                  <div className="sim-posted-date">Jun 12 · 40kg available</div>
                  <div className="sim-posted-price">€3.50/kg</div>
                  <div className="sim-incoming">
                    <div className="sim-incoming-dot" />
                    <span>New booking request!</span>
                  </div>
                  <div className="sim-request-card">
                    <div className="sim-request-avatar">F</div>
                    <div className="sim-request-info">
                      <strong>Fatima Z. wants to send 8kg</strong>
                      <span>Clothes & medicine · Hamburg pickup</span>
                    </div>
                  </div>
                  <div className="sim-request-earn">
                    You'll earn: <strong>€{(8 * 3.5 * 0.90).toFixed(2)}</strong>
                    <span style={{ fontSize: 10, color: 'var(--sim-olive)', marginLeft: 4 }}>(after 10% fee)</span>
                  </div>
                  <button className="sim-cta" onClick={confirmBooking}>Accept Booking →</button>
                </div>
              </div>
            )}

            {/* ── STEP 2 (SENDER): Booking confirm ── */}
            {simStep === 2 && role === 'sender' && (
              <div className="sim-pane sim-pane-enter">
                <div className="sim-screen-header">
                  <button className="sim-back" onClick={() => setSimStep(1)}>←</button>
                  <span>Confirm booking</span>
                </div>
                <div className="sim-confirm-card">
                  <div className="sim-confirm-row">
                    <span>Transporter</span>
                    <strong>{selectedTrip?.transporter} ⭐ {selectedTrip?.rating}</strong>
                  </div>
                  <div className="sim-confirm-row">
                    <span>Route</span>
                    <strong>{selectedTrip?.from} → {selectedTrip?.to}</strong>
                  </div>
                  <div className="sim-confirm-row">
                    <span>Departure</span>
                    <strong>{selectedTrip?.date}</strong>
                  </div>
                  <div className="sim-confirm-row">
                    <span>Weight</span>
                    <strong>{weight} kg</strong>
                  </div>
                  <div className="sim-confirm-divider" />
                  <div className="sim-confirm-row sim-confirm-price-row">
                    <span>Total (incl. 10% fee)</span>
                    <strong className="sim-confirm-price">€{price}</strong>
                  </div>
                  <div className="sim-savings-pill">
                    💚 Saving €{savings} vs DHL ({savingsPct}% cheaper)
                  </div>
                </div>
                <div className="sim-fake-payment">
                  <div className="sim-card-row">
                    <span className="sim-card-icon">💳</span>
                    <span className="sim-card-num">•••• •••• •••• 4242</span>
                    <span className="sim-card-brand">VISA</span>
                  </div>
                </div>
                <button className="sim-cta" onClick={confirmBooking}>
                  Pay €{price} securely →
                </button>
              </div>
            )}

            {/* ── STEP 3: Chat ── */}
            {simStep === 3 && (
              <div className="sim-pane sim-pane-enter">
                <div className="sim-screen-header">
                  <span className="sim-chat-avatar">{role === 'sender' ? selectedTrip?.avatar || 'M' : 'F'}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{role === 'sender' ? selectedTrip?.transporter : 'Fatima Z.'}</div>
                    <div className="sim-chat-online">● Online</div>
                  </div>
                </div>
                <div className="sim-chat" ref={chatRef}>
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`sim-bubble sim-bubble-${msg.from} sim-bubble-enter`}>
                      {msg.text}
                    </div>
                  ))}
                  {chatMessages.length === 0 && (
                    <div className="sim-typing">
                      <span /><span /><span />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 4: Status tracker ── */}
            {simStep === 4 && (
              <div className="sim-pane sim-pane-enter">
                <div className="sim-screen-header">
                  <span>Tracking your goods</span>
                </div>
                <div className="sim-tracker">
                  {STATUS_STEPS.map((step, i) => (
                    <div key={i} className={`sim-track-step${statusStep > i ? ' done' : ''}${statusStep === i + 1 ? ' active' : ''}`}>
                      <div className="sim-track-line-wrap">
                        <div className={`sim-track-dot${statusStep > i ? ' done' : ''}`}>
                          {statusStep > i ? '✓' : step.icon}
                        </div>
                        {i < STATUS_STEPS.length - 1 && (
                          <div className={`sim-track-line${statusStep > i + 1 ? ' done' : ''}`} />
                        )}
                      </div>
                      <div className="sim-track-text">
                        <strong>{step.label}</strong>
                        <span>{step.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── STEP 5: Success ── */}
            {simStep === 5 && (
              <div className="sim-pane sim-pane-enter sim-success-pane">
                <div className="sim-success-burst">🎉</div>
                <h3 className="sim-success-title">
                  {role === 'sender' ? 'Package delivered!' : 'You earned €25.20!'}
                </h3>
                <p className="sim-success-sub">
                  {role === 'sender'
                    ? `Saved €${savings} vs DHL. Your mother got her package in 2 days.`
                    : 'Fatima left you a 5-star review. Your rating is now 4.9 ⭐'}
                </p>
                <div className="sim-review-stars">★★★★★</div>
                <div className="sim-success-quote">
                  {role === 'sender'
                    ? '"Incredible service. Quick, trustworthy, half the price of DHL!"'
                    : '"Perfect transporter — on time, careful with my goods. Highly recommend!"'}
                </div>
                <button className="sim-restart" onClick={restart}>
                  ↩ Try the other role
                </button>
              </div>
            )}

          </div>

          {/* Phone bottom bar */}
          <div className="sim-phone-bar">
            <div /><div /><div />
          </div>
        </div>

        {/* Side info panel */}
        <div className="sim-side">
          {simStep === 0 && (
            <div className="sim-side-intro sim-side-enter">
              <div className="sim-side-icon">👆</div>
              <h3>See it in action</h3>
              <p>Pick your role and walk through a real booking — from finding a trip to delivery confirmation.</p>
              <div className="sim-side-facts">
                <div className="sim-fact"><span>⚡</span> 2 minute experience</div>
                <div className="sim-fact"><span>🔒</span> No signup needed</div>
                <div className="sim-fact"><span>💯</span> Real app interface</div>
              </div>
            </div>
          )}

          {simStep === 1 && role === 'sender' && (
            <div className="sim-side-enter">
              <h3>Find a trusted carrier</h3>
              <p>Browse real people flying to your destination. Every transporter is verified and reviewed by the community.</p>
              <div className="sim-side-tip">
                <span>💡</span>
                <span>Drag the weight slider and watch the price update in real time — and compare it to DHL.</span>
              </div>
              <div className="sim-dhl-big">
                <div className="sim-dhl-row">
                  <span>DHL {weight}kg</span>
                  <strong className="sim-dhl-bad">€{dhlPrice}</strong>
                </div>
                <div className="sim-dhl-row">
                  <span>Transportily {weight}kg</span>
                  <strong className="sim-dhl-good">€{selectedTrip ? (weight * selectedTrip.priceKg * 1.10).toFixed(0) : (weight * 3.5 * 1.10).toFixed(0)}</strong>
                </div>
                <div className="sim-dhl-bar-wrap">
                  <div className="sim-dhl-bar-label">You save</div>
                  <div className="sim-dhl-bar">
                    <div
                      className="sim-dhl-bar-fill"
                      style={{ width: `${Math.min(100, savingsPct || 55)}%` }}
                    />
                  </div>
                  <div className="sim-dhl-pct">{savingsPct || 55}% cheaper</div>
                </div>
              </div>
            </div>
          )}

          {simStep === 1 && role === 'transporter' && (
            <div className="sim-side-enter">
              <h3>Earn on every trip</h3>
              <p>You were already flying to Tunis. Now your empty suitcase space becomes income.</p>
              <div className="sim-earn-calc">
                <div className="sim-earn-row">
                  <span>8kg booking</span>
                  <strong>€{(8 * 3.5).toFixed(2)}</strong>
                </div>
                <div className="sim-earn-row">
                  <span>Platform fee (10%)</span>
                  <strong style={{ color: 'var(--sim-red)' }}>-€{(8 * 3.5 * 0.10).toFixed(2)}</strong>
                </div>
                <div className="sim-earn-divider" />
                <div className="sim-earn-row sim-earn-total">
                  <span>You receive</span>
                  <strong>€{(8 * 3.5 * 0.90).toFixed(2)}</strong>
                </div>
              </div>
              <div className="sim-side-tip">
                <span>✈️</span>
                <span>A round trip with 2 bookings covers most of your flight cost.</span>
              </div>
            </div>
          )}

          {simStep === 2 && (
            <div className="sim-side-enter">
              <h3>Secure payment</h3>
              <p>Payment is held by Transportily and only released to the transporter once delivery is confirmed.</p>
              <div className="sim-side-tip"><span>🔒</span><span>Your money is protected until your goods arrive safely.</span></div>
              <div className="sim-side-tip"><span>💳</span><span>Stripe-secured checkout. No card details shared with the transporter.</span></div>
            </div>
          )}

          {simStep === 3 && (
            <div className="sim-side-enter">
              <h3>Chat in the app</h3>
              <p>Coordinate pickup, share photos, ask questions — all within Transportily. No need to share personal phone numbers.</p>
              <div className="sim-side-tip"><span>📸</span><span>Both parties upload photos of the goods at pickup — your proof if anything goes wrong.</span></div>
            </div>
          )}

          {simStep === 4 && (
            <div className="sim-side-enter">
              <h3>Track every step</h3>
              <p>Live status updates from collection to delivery. You'll know exactly where your goods are.</p>
              <div className="sim-side-tip"><span>🔔</span><span>Push notifications at every status change — no need to refresh.</span></div>
            </div>
          )}

          {simStep === 5 && (
            <div className="sim-side-enter">
              <h3>Ready to do this for real?</h3>
              <p>Transportily launches soon. Join the waitlist and be among the first to send or carry goods — with perks for early members.</p>
              <a href="#signup" className="sim-join-btn">🚀 Join the Waitlist →</a>
              <div className="sim-side-tip" style={{ marginTop: 16 }}>
                <span>🏅</span>
                <span>First 100 members get 50% platform fee off — for life.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}